import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, Clock, User, Search, CheckCircle, XCircle, Trash2, ArrowLeft, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { authFetch, getCurrentUser } from '../../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  called: { label: 'Appele', color: 'bg-green-100 text-green-800' },
  no_answer: { label: 'Pas de reponse', color: 'bg-red-100 text-red-800' },
};

const timeLabels = { matin: 'Matin (8h-12h)', 'apres-midi': 'Apres-midi (12h-17h)', soir: 'Soir (17h-19h)' };
const centerLabels = { diplomante: 'Diplomante', qualifiante: 'Qualifiante', certifiante: 'Certifiante', linguistique: 'Linguistique' };

export default function AdminCallbacks() {
  const [callbacks, setCallbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const fetchCallbacks = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/callbacks`);
      if (res.status === 401) { navigate('/admin'); return; }
      setCallbacks(await res.json());
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    const user = getCurrentUser();
    if (!user.role) { navigate('/admin'); return; }
    fetchCallbacks();
  }, [fetchCallbacks, navigate]);

  const updateStatus = async (id, status) => {
    try {
      await authFetch(`${API_URL}/api/callbacks/${id}?status=${status}`, { method: 'PATCH' });
      setCallbacks(prev => prev.map(c => c.id === id ? { ...c, status } : c));
      toast.success('Statut mis a jour');
    } catch { toast.error('Erreur de mise a jour'); }
  };

  const deleteCallback = async (id) => {
    if (!window.confirm('Supprimer cette demande de rappel ?')) return;
    try {
      await authFetch(`${API_URL}/api/callbacks/${id}`, { method: 'DELETE' });
      setCallbacks(prev => prev.filter(c => c.id !== id));
      toast.success('Demande supprimee');
    } catch { toast.error('Erreur de suppression'); }
  };

  const filtered = callbacks.filter(c => {
    if (filter !== 'all' && c.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return c.name.toLowerCase().includes(q) || c.phone.includes(q);
    }
    return true;
  });

  const pendingCount = callbacks.filter(c => c.status === 'pending').length;

  const renderActions = (cb) => {
    if (cb.status === 'pending') {
      return (
        <>
          <button onClick={() => updateStatus(cb.id, 'called')} className="p-1.5 rounded-lg hover:bg-green-50 text-green-600" title="Marquer comme appele" data-testid={`mark-called-${cb.id}`}><CheckCircle size={18} /></button>
          <button onClick={() => updateStatus(cb.id, 'no_answer')} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" title="Pas de reponse" data-testid={`mark-no-answer-${cb.id}`}><XCircle size={18} /></button>
        </>
      );
    }
    if (cb.status === 'no_answer') {
      return (
        <button onClick={() => updateStatus(cb.id, 'pending')} className="p-1.5 rounded-lg hover:bg-yellow-50 text-yellow-600" title="Remettre en attente" data-testid={`mark-pending-${cb.id}`}><RefreshCw size={18} /></button>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-callbacks-page">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard"><Button variant="ghost" size="sm" data-testid="back-to-dashboard"><ArrowLeft size={18} className="mr-2" /> Dashboard</Button></Link>
            <div className="flex items-center gap-2">
              <Phone className="text-[#DC143C]" size={24} />
              <h1 className="text-xl font-bold text-[#003366]">Demandes de rappel</h1>
              {pendingCount > 0 && <span className="bg-[#DC143C] text-white text-xs font-bold px-2 py-1 rounded-full" data-testid="pending-badge">{pendingCount}</span>}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchCallbacks} data-testid="refresh-btn"><RefreshCw size={16} className="mr-2" /> Actualiser</Button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 text-gray-400" size={18} />
            <input type="text" placeholder="Rechercher par nom ou telephone..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-[#003366]" data-testid="search-input" />
          </div>
          <div className="flex gap-2">
            {['all', 'pending', 'called', 'no_answer'].map(f => (
              <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === f ? 'bg-[#003366] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-testid={`filter-${f}`}>
                {f === 'all' ? 'Tous' : statusConfig[f]?.label}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="text-center py-12 text-gray-500">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border"><Phone className="mx-auto text-gray-300 mb-4" size={48} /><p className="text-gray-500">Aucune demande de rappel</p></div>
        ) : (
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="callbacks-table">
                <thead><tr className="bg-gray-50 border-b">
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Nom</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Telephone</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Email</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Centre</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Creneau</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Date</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Statut</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Actions</th>
                </tr></thead>
                <tbody>
                  {filtered.map((cb) => {
                    const st = statusConfig[cb.status] || statusConfig.pending;
                    return (
                      <tr key={cb.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`callback-row-${cb.id}`}>
                        <td className="px-6 py-4"><div className="flex items-center gap-3"><div className="w-8 h-8 bg-[#003366] rounded-full flex items-center justify-center text-white text-sm font-bold">{cb.name.charAt(0).toUpperCase()}</div><span className="font-medium text-[#003366]">{cb.name}</span></div></td>
                        <td className="px-6 py-4"><a href={`tel:${cb.phone}`} className="text-[#DC143C] font-medium hover:underline">{cb.phone}</a></td>
                        <td className="px-6 py-4 text-sm">{cb.email ? <a href={`mailto:${cb.email}`} className="text-[#003366] hover:underline">{cb.email}</a> : '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{centerLabels[cb.center] || cb.center || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{timeLabels[cb.preferred_time] || cb.preferred_time}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{new Date(cb.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="px-6 py-4"><span className={`px-3 py-1 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span></td>
                        <td className="px-6 py-4"><div className="flex items-center gap-2">
                          {renderActions(cb)}
                          <button onClick={() => deleteCallback(cb.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400" title="Supprimer" data-testid={`delete-callback-${cb.id}`}><Trash2 size={18} /></button>
                        </div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
